import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { TextField, SelectField, DateField } from "@/components/form";
import { formatPhone, formatSsn } from "@/utils/format";
import { toDateInput } from "@/utils/dates";
import { onboarding } from "@/features/onboarding/api/onboarding.api";
import { applyScan, scanLabels, SCAN_KEPT_MESSAGE, type ScannedField, useDocScan, type ScanOutcome, StepHeading, FileDrop, SubHeading, ScanStatus, useZipAutoFill, splitGivenNames, trimmed } from "@/features/onboarding/components/wizard";
import { STATE_OPTIONS, stateCode } from "@/utils/states";
import { PhotoField } from "./PhotoField";
import { type StepProps, type FormState } from "./schema";

export function OwnerStep({ form, dlFile, onDlFile, dlError, dlPreviousNote, onScanned, onBusyChange }: StepProps & { dlFile: File | null; onDlFile: (f: File | null) => void; dlError?: string | null; dlPreviousNote?: string | null; onScanned?: () => void; onBusyChange?: (busy: boolean) => void }) {

  useZipAutoFill(form.watch("ownerHomeZip"), (st, ci) => {
    const sv = { shouldValidate: form.formState.isSubmitted };
    if (st) form.setValue("ownerHomeState", st, sv);
    if (ci) form.setValue("ownerHomeCity", ci, sv);
  });
  const c = form.control;

  const [revealed, setRevealed] = useState(
    () => Boolean(dlFile || form.getValues("ownerFirstName") || form.getValues("ownerLastName") || form.getValues("ownerSsn")));
  const hasOwnerError = Object.keys(form.formState.errors).some((k) => k.startsWith("owner"));
  useEffect(() => { if (hasOwnerError) setRevealed(true); }, [hasOwnerError]);
  const showFields = revealed;

  const dl = useDocScan({
    reading: "Reading your license…",
    failed: "Something went wrong reading your license. Please fill in the fields below manually.",
    onFile: onDlFile,
    onBusyChange,
    onPick: () => setRevealed(true),
    run: async (file, onlyEmpty): Promise<ScanOutcome> => {
      const d = await onboarding.scan(file, "driverLicense");
      const { first, middle } = splitGivenNames(d.firstName, d.middleName);
      const scanned: ScannedField<FormState>[] = [
        ["ownerFirstName", first, "first name"],
        ["ownerMiddleName", middle, "middle name"],
        ["ownerLastName", trimmed(d.lastName), "last name"],
        ["ownerDateOfBirth", toDateInput(d.dateOfBirth), "date of birth"],
        ["ownerHomeAddressLine1", trimmed(d.addressLine1), "address"],
        ["ownerHomeCity", trimmed(d.city), "city"],
        ["ownerHomeState", stateCode(d.state), "state"],
        ["ownerHomeZip", trimmed(d.zip), "ZIP"],
      ];
      const { read, applied } = applyScan(form, scanned, { shouldValidate: form.formState.isSubmitted, onlyEmpty });
      if (read.length === 0) {
        return { kind: "error", msg: "We couldn't read anything off that photo. Try a clearer, well-lit shot, or fill in the fields below manually." };
      }
      onScanned?.();
      return {
        kind: "success",
        msg: applied.length === 0 ? SCAN_KEPT_MESSAGE : `Pre-filled ${scanLabels(applied).join(", ")}. Please review each field and fix anything that's off.`,
      };
    },
  });

  return (
    <div className="space-y-5">
      <StepHeading
        title="Principal information"
        subtitle="We verify the identity of the principal owner."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField control={c} name="ownerSsn" label="Social Security Number" required inputMode="numeric" maxLength={11} placeholder="XXX-XX-XXXX" format={formatSsn} hint="9 digits, formatted as XXX-XX-XXXX" />
        <TextField control={c} name="ownerPhone" label="Phone" required type="tel" inputMode="tel" maxLength={14} placeholder="(555) 123-4567" format={formatPhone} />
      </div>

      <PhotoField label="Take a photo of your driver's license" required>
        <FileDrop prompt="Take a photo or upload" busy={dl.busy} file={dlFile} error={dlError} previousNote={dlPreviousNote} onFile={(f) => dl.pick(f, !!dlPreviousNote)} onRemove={dl.clear} />
        <ScanStatus msg={dl.msg} kind={dl.kind} onRetry={dl.retry} />
      </PhotoField>

      {!showFields && (
        <Button type="button" variant="link" onClick={() => setRevealed(true)} className="px-0 text-muted-foreground">
          Enter details manually instead
        </Button>
      )}

      {showFields && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <TextField control={c} name="ownerFirstName" label="First name" required />
            <TextField control={c} name="ownerMiddleName" label="Middle" />
            <TextField control={c} name="ownerLastName" label="Last name" required />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DateField control={c} name="ownerDateOfBirth" label="Date of birth" required disableFuture defaultYear={1990} />
          </div>

          <SubHeading>Home address</SubHeading>
          <div className="space-y-4">
            <TextField control={c} name="ownerHomeAddressLine1" label="Street address" required placeholder="123 Main St" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <TextField control={c} name="ownerHomeZip" label="Zipcode" required inputMode="numeric" maxLength={5} placeholder="94103" format={(v) => v.replace(/\D/g, "").slice(0, 5)} />
              <SelectField control={c} name="ownerHomeState" label="State" required placeholder="Select" options={STATE_OPTIONS} />
              <TextField control={c} name="ownerHomeCity" label="City" required placeholder="San Francisco" className="col-span-2 sm:col-span-1" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
